import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import ShowChartIcon from '@mui/icons-material/BarChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import TableChartIcon from '@mui/icons-material/TableChart';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { generatePastelColor, generateSaturatedColor } from '../../Utils/colorUtils';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useSnackbar } from '../../Utils/SnackBarContext';
import TimeEventTableViewer from './TimeEventTableViewer';
import TimeEventProof from './TimeEventProof';
import { useParams } from 'react-router-dom';
import { LineChartComponent, PieChartComponent } from '../Chart'; // Import both chart components
import '../../styles/Analytics.css'; // Import the CSS file for chart
import { FormControl, Select, MenuItem, FormControlLabel, Switch, InputLabel, Paper, Divider, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination } from '@mui/material';
import { useDashboard } from '../Dashboard/DashboardContext';
import { TagTooltip, UnifiedAnswerRenderer } from '../Common';
import DeleteConfirmationDialog from '../Common/DeleteConfirmationDialog';
import CloseButton from '../Common/CloseButton';
import { isLikelyImageData } from '../Common/UnifiedAnswerRenderer';
import { useTranslation } from 'react-i18next';
import { deleteTimeeventTag, updateTimeeventTag } from '../../Utils/timeEventsApi';
import { isErrorStatus } from '../../constants/timeEventStatus';
import { fetchProjectChartData } from '../../Utils/projectDataApi';
import { hasUserTagVariableReservedChars } from '../../Utils/tagUtils';
import {
  VIEWER_TAG_HOVER_EVENT,
  buildTagHoverEventDetail,
  doesTagHoverDetailMatch,
} from '../Viewer/TagHighlightUtils';
import { getViewerTypeFromFilename, useFileViewer } from '../Viewer/FileViewerProvider';

// Theme colors - matching Sidebarinfo 
const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const selectedColor = '#4545a0';
const lightPurple = '#f0f0ff';

// Add helper functions for chart aggregation
const getWeekStartDate = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff)).toISOString().split('T')[0];
};

const generateAllPeriods = (startDate, endDate, aggregation) => { 
  const periods = [];
  let current = new Date(startDate);
  const end = new Date(endDate);
  
  while (current <= end) {
    let period;
    if (aggregation === 'day') {
      period = current.toISOString().split('T')[0];
      current.setDate(current.getDate() + 1);
    } else if (aggregation === 'week') {
      period = getWeekStartDate(current);
      current.setDate(current.getDate() + 7);
    } else if (aggregation === 'month') {
      period = current.toISOString().slice(0,7);
      current.setMonth(current.getMonth() + 1);
    }
    periods.push(period);
  }
  return periods;
};

const StyledBox = styled(Box)(({ theme, backgroundColor }) => ({
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)} ${theme.spacing(0.5)} ${theme.spacing(0.5)}`, // top, right, bottom, left - reduced left padding
  background: `linear-gradient(135deg, white 0%, ${backgroundColor || lightPurple}15 100%)`,
  border: 'none',
  borderLeft: `4px solid ${backgroundColor || primaryColor}`,
  boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
  cursor: 'pointer',  '&:hover': {
    background: `linear-gradient(135deg, ${lightPurple}30 0%, ${backgroundColor || lightPurple}18 100%)`,
    boxShadow: '0 2px 8px rgba(87, 87, 176, 0.12)',
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0 1px 4px rgba(87, 87, 176, 0.16)',
  },
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'flex-start',
  alignItems: 'center',
  marginRight: "8px",
  marginTop: "4px",
  marginBottom: "2px",
  minWidth: 64,
  minHeight: 32,
  maxWidth: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  borderRadius: '12px',
  transition: 'transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease, border-color 120ms ease',
  touchAction: 'manipulation',
  overflow: 'hidden', // Prevent content from overflowing the box
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    minHeight: 44,
    marginRight: 0,
    padding: `${theme.spacing(0.75)} ${theme.spacing(1)}`,
  },
}));



// Check if string is an empty JSON array
const isEmptyJsonArray = (str) => {
  if (Array.isArray(str)) {
    return str.length === 0;
  }
  try {
    const json = JSON.parse(str);
    return Array.isArray(json) && json.length === 0;
  } catch (e) {
    return false;
  }
};

// Function to format JSON for display
const formatJsonForDisplay = (jsonString, maxLength = 30) => {
  try {
    if (typeof jsonString === 'object' && jsonString !== null) {
      const shortValue = JSON.stringify(jsonString);
      return {
        isJson: true,
        short: shortValue.length > maxLength ? `${shortValue.substring(0, maxLength)}...` : shortValue,
        full: jsonString,
        raw: jsonString,
      };
    }
    // First check if the string is a number or boolean primitive
    if (!isNaN(jsonString) || jsonString === 'true' || jsonString === 'false') {
      return { isJson: false, short: jsonString, full: jsonString };
    }
    
    // Try to parse as JSON
    const parsed = JSON.parse(jsonString);
    
    // Only treat as JSON if it's an object or array, not primitive values
    if ((typeof parsed !== 'object' && !Array.isArray(parsed)) || parsed === null) {
      return { isJson: false, short: jsonString, full: jsonString };
    }
    
    // For tooltip or detailed view
    const fullFormatted = parsed;
    
    // For inline display (truncated)
    let shortDisplay = JSON.stringify(parsed);
    if (shortDisplay.length > maxLength) {
      shortDisplay = shortDisplay.substring(0, maxLength) + '...';
    }
    
    return { isJson: true, short: shortDisplay, full: fullFormatted, raw: parsed };
  } catch (e) {
    return { isJson: false, short: jsonString, full: jsonString };
  }
};

const normalizeTagValue = (value) => (value === null || value === undefined ? '' : String(value));

const normalizeTagCoordinates = (coordinates) => {
  if (!coordinates) {
    return [];
  }

  let parsedCoordinates = coordinates;
  if (typeof coordinates === 'string') {
    try {
      parsedCoordinates = JSON.parse(coordinates);
    } catch (error) {
      return [];
    }
  }

  const coordinateItems = Array.isArray(parsedCoordinates) ? parsedCoordinates : [parsedCoordinates];
  return coordinateItems
    .map((item) => {
      const bbox = item?.bbox_percent;
      const page = Number(item?.page || 1);
      if (!Array.isArray(bbox) || bbox.length !== 4 || !Number.isFinite(page)) {
        return null;
      }

      const normalizedBbox = bbox.map((value) => Number(value));
      if (normalizedBbox.some((value) => !Number.isFinite(value))) {
        return null;
      }

      return {
        page,
        bbox: normalizedBbox,
        source: item?.source || '',
        text: item?.text || '',
      };
    })
    .filter(Boolean);
};

const formatCoordinateNumber = (value) => Number(value).toFixed(4);

const TimeEventTag = ({ tag, onDelete, onUpdate, timeevent, canManageProject = false, isProcessing = false, showFullValue = false, validationLockToken = '', openViewerOnHover = true, enableTouchHighlight = false }) => {
  // console.log('Rendering TimeEventTag:', tag);
  const { filteredTimeEvents } = useDashboard();
  const { t } = useTranslation();
  const [isEditingTag, setIsEditingTag] = useState(false);
  const [isSavingTag, setIsSavingTag] = useState(false);
  const [editTarget, setEditTarget] = useState('reply');
  const [editedVariable, setEditedVariable] = useState(normalizeTagValue(tag.variable));
  const [editedReply, setEditedReply] = useState(normalizeTagValue(tag.reply));
  const [editVariableError, setEditVariableError] = useState('');
  const [openProofDialog, setOpenProofDialog] = useState(false);
  const { openSnackbar } = useSnackbar();
  const { scheduleFileViewerOpen, cancelScheduledViewerOpen } = useFileViewer();
  const { caseid } = useParams(); // Get caseid from URL params
  
  // Chart related state
  const [showChart, setShowChart] = useState(false);
  const [aggregation, setAggregation] = useState('day');
  const [timeRange, setTimeRange] = useState('24month');
  const [showEmptyDates, setShowEmptyDates] = useState(true);
  const [chartType, setChartType] = useState('line'); // Add chart type state
  
  // Table pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State for server-side chart data and chart type
  const [serverChartData, setServerChartData] = useState([]);
  const [serverTimeSeriesData, setServerTimeSeriesData] = useState([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);

  // State for text data detection from server response
  const [isTextData, setIsTextData] = useState(false);

  // State for image viewer dialog
  const [openImageViewer, setOpenImageViewer] = useState(false);
  const [imageToShow, setImageToShow] = useState('');

  // State for delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeletingTag, setIsDeletingTag] = useState(false);
  const normalizedVariable = normalizeTagValue(tag?.variable);
  const normalizedReply = normalizeTagValue(tag?.reply);
  const isLoadingState = !isErrorStatus(timeevent?.eventstatus)
    && (isProcessing || Boolean(tag?.loading));
  const displayVariable = isEditingTag ? editedVariable : normalizedVariable;
  const displayReply = isEditingTag ? editedReply : normalizedReply;
  const hasDisplayableReply = String(displayReply).trim() !== '';
  const loadingLabel = t('timeevent.loading_processing', 'Processing...');
  const needsReview = !isLoadingState && (
    tag?.needs_review === true ||
    tag?.needs_review === 1 ||
    tag?.needs_review === '1' ||
    String(tag?.needs_review || '').toLowerCase() === 'true'
  );
  const rawConfidence = tag?.confidence;
  const confidenceValue = rawConfidence === null || rawConfidence === undefined || rawConfidence === ''
    ? NaN
    : Number(rawConfidence);
  const hasConfidence = Number.isFinite(confidenceValue);
  const confidenceLabel = hasConfidence ? `${Math.round(confidenceValue * 100)}%` : null;
  const confidenceDisplayLabel = confidenceLabel || t('timeevent.tag_confidence_unavailable', 'Not available');
  const confidenceReason = normalizeTagValue(tag?.review_reason).trim();
  const confidenceReasonLabel = t('timeevent.tag_confidence_reason', 'Reason');
  const tagTitle = confidenceReason
    ? `${t('timeevent.tag_confidence', 'Confidence')}: ${confidenceDisplayLabel} | ${confidenceReasonLabel}: ${confidenceReason}`
    : `${t('timeevent.tag_confidence', 'Confidence')}: ${confidenceDisplayLabel}`;
  const reviewTooltipLabel = tag?.review_reason
    ? `${t('timeevent.tag_needs_review', 'Needs review')}: ${tag.review_reason}`
    : t('timeevent.tag_needs_review', 'Needs review');
  const [viewerHoveredTag, setViewerHoveredTag] = useState(null);
  const [isTouchHighlightSelected, setIsTouchHighlightSelected] = useState(false);
  const touchHighlightSelectedRef = useRef(false);
  const [activeEvidenceIndex, setActiveEvidenceIndex] = useState(0);
  const coordinateItems = useMemo(
    () => normalizeTagCoordinates(tag?.coordinates),
    [tag?.coordinates]
  );
  const dispatchTimeEventTagHover = useCallback((hoveredTag, occurrenceIndex = null) => {
    if (typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(new CustomEvent(VIEWER_TAG_HOVER_EVENT, {
      detail: hoveredTag
        ? buildTagHoverEventDetail(hoveredTag, {
            id: timeevent?.id,
            timeeventid: timeevent?.timeeventid,
          }, occurrenceIndex)
        : null,
    }));
  }, [timeevent?.id, timeevent?.timeeventid]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleViewerTagHover = (event) => {
      const detail = event?.detail || null;
      if (!detail) {
        setViewerHoveredTag(null);
        touchHighlightSelectedRef.current = false;
        setIsTouchHighlightSelected(false);
        return;
      }

      const currentTag = {
        id: tag?.id,
        timeeventid: tag?.timeeventid ?? tag?.timeeventId,
        variable: normalizedVariable,
      };
      const currentTimeevent = {
        id: timeevent?.id,
        timeeventid: timeevent?.timeeventid,
      };

      const matchesCurrentTag = doesTagHoverDetailMatch(detail, currentTag, currentTimeevent);
      setViewerHoveredTag(matchesCurrentTag ? detail : null);
      if (matchesCurrentTag) {
        const requestedOccurrence = Number(detail.occurrenceIndex);
        if (
          Number.isInteger(requestedOccurrence) &&
          requestedOccurrence >= 0 &&
          requestedOccurrence < coordinateItems.length
        ) {
          setActiveEvidenceIndex(requestedOccurrence);
        }
      }
      if (!matchesCurrentTag) {
        touchHighlightSelectedRef.current = false;
        setIsTouchHighlightSelected(false);
      }
    };

    window.addEventListener(VIEWER_TAG_HOVER_EVENT, handleViewerTagHover);
    return () => window.removeEventListener(VIEWER_TAG_HOVER_EVENT, handleViewerTagHover);
  }, [coordinateItems.length, normalizedVariable, tag?.id, tag?.timeeventId, tag?.timeeventid, timeevent?.id, timeevent?.timeeventid]);
  const isViewerHoveredTag = Boolean(viewerHoveredTag);

  useEffect(() => {
    touchHighlightSelectedRef.current = false;
    setIsTouchHighlightSelected(false);
  }, [normalizedVariable, tag?.id, timeevent?.id, timeevent?.timeeventid]);

  useEffect(() => () => {
    if (enableTouchHighlight && touchHighlightSelectedRef.current) {
      dispatchTimeEventTagHover(null);
    }
  }, [dispatchTimeEventTagHover, enableTouchHighlight]);

  const scheduleViewerOpen = useCallback(() => {
    const eventSource = timeevent?.eventsource;
    const eventId = timeevent?.id ?? timeevent?.timeeventid;
    if (!eventSource || !eventId || timeevent?.isExist === false || timeevent?.isExist === 0) {
      return;
    }
    const filename = String(eventSource).split(/[\\/]/).pop() || 'document';
    scheduleFileViewerOpen({
      type: getViewerTypeFromFilename(filename),
      filename,
      eventId,
      filePath: eventSource,
      timeevent,
    });
  }, [scheduleFileViewerOpen, timeevent]);

  const handleOpenImageViewer = (src) => {
    setImageToShow(src);
    setOpenImageViewer(true);
  };

  const handleCloseImageViewer = () => {
    setOpenImageViewer(false);
    setImageToShow('');
  };

  // Delete confirmation handlers
  const handleDeleteClick = () => {
    if (!canManageProject || isLockedWidgetTag || isLoadingState) {
      return;
    }
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (isDeletingTag) {
      return;
    }

    const eventId = tag?.timeeventid ?? timeevent?.id;
    if (canManageProject && eventId && tag && onDelete && openSnackbar) {
      setIsDeletingTag(true);
      try {
        await deleteTimeeventTag(eventId, tag.id, { lockToken: validationLockToken });
        setOpenDeleteDialog(false);
        openSnackbar("Deleted successfully.", 1000);
        onDelete(tag);
      } catch (error) {
        openSnackbar(error.message || "Unable to delete tag.", 3000, "error");
      } finally {
        setIsDeletingTag(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    if (isDeletingTag) {
      return;
    }
    setOpenDeleteDialog(false);
  };

  // Process the reply to check if it's JSON
  const formattedReply = useMemo(() => 
    formatJsonForDisplay(tag.reply ?? ''),
    [tag.reply]
  );
  
  // Check if the reply is an empty JSON array
  const isEmptyArray = useMemo(() => 
    isEmptyJsonArray(tag.reply ?? ''),
    [tag.reply]
  );

  // Check if this tag contains image data
  const isImageTag = useMemo(() => 
    isLikelyImageData(tag?.type),
    [tag?.type]
  );
  const canEditValue = !formattedReply.isJson && !isImageTag && !isLoadingState;
  const isLockedWidgetTag = String(tag?.tag_type || '').toLowerCase() === 'widget';
  const handleTouchHighlight = useCallback((event) => {
    event.stopPropagation();
    if (!enableTouchHighlight || isLoadingState || !coordinateItems.length) {
      return;
    }

    const nextSelected = !touchHighlightSelectedRef.current;
    touchHighlightSelectedRef.current = nextSelected;
    setIsTouchHighlightSelected(nextSelected);
    dispatchTimeEventTagHover(nextSelected ? tag : null, nextSelected ? activeEvidenceIndex : null);
  }, [activeEvidenceIndex, coordinateItems.length, dispatchTimeEventTagHover, enableTouchHighlight, isLoadingState, tag]);
  const selectEvidence = useCallback((nextIndex) => {
    if (!coordinateItems.length) return;
    const normalizedIndex = (nextIndex + coordinateItems.length) % coordinateItems.length;
    setActiveEvidenceIndex(normalizedIndex);
    dispatchTimeEventTagHover(tag, normalizedIndex);
  }, [coordinateItems.length, dispatchTimeEventTagHover, tag]);

  // Collect all data for the table view
  const tableData = useMemo(() => {
    if (!showChart) return [];
    
    const data = [];
    
    filteredTimeEvents.forEach(timeevent => {
      const eventDateStr = timeevent.eventtime.split(' ')[0]; // Get the date part
      const eventDate = new Date(eventDateStr);
      const isValidDate = !isNaN(eventDate.getTime()); // Proper date validation

      if (!isValidDate) return;

      const customFields = timeevent.customFields || [];
      const currentField = customFields.find((f) => f.variable === tag.variable);
      
      if (!currentField || !currentField.reply) return;
      
      const value = currentField.reply;
      const documentName = timeevent.eventsource || 'No Document Name';
      
      // Convert value to string for parsing if it's an array
      const valueForParsing = Array.isArray(value) ? JSON.stringify(value) : value;
      
      data.push({
        date: eventDateStr,
        formattedDate: new Date(eventDateStr).toLocaleDateString(),
        documentName: documentName,
        value: value,
        numericValue: !isNaN(parseFloat(valueForParsing)) ? parseFloat(valueForParsing) : null
      });
    });
    
    // Sort by date descending (most recent first)
    data.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    return data;
  }, [filteredTimeEvents, tag.variable, showChart]);

  // Chart data calculation for client-side rendering when server data isn't available yet
  const chartData = useMemo(() => {
    if (!showChart) return [];
    
    const data = filteredTimeEvents.reduce((acc, timeevent) => {
      const eventDateStr = timeevent.eventtime.split(' ')[0]; // Get the date part
      const eventDate = new Date(eventDateStr);
      const isValidDate = !isNaN(eventDate.getTime()); // Proper date validation

      const customFields = timeevent.customFields || [];
      const currentField = customFields.find((f) => f.variable === tag.variable);
      const value_ = currentField?.reply || 0;
      const documentName = timeevent.eventsource || 'No Document Name';

      // Convert value to string for parsing if it's an array
      const valueForParsing = Array.isArray(value_) ? JSON.stringify(value_) : value_;

      if (value_ !== '' && !isNaN(parseFloat(valueForParsing)) && isFinite(valueForParsing) && parseFloat(valueForParsing) > 0 && isValidDate) {
        const formattedDate = eventDate.toISOString().split('T')[0];
        if (!acc[formattedDate]) {
          acc[formattedDate] = { totalValue: 0, count: 0, documentNames: [], values: [] };
        }
        acc[formattedDate].totalValue += parseFloat(value_);
        acc[formattedDate].count += 1;
        acc[formattedDate].documentNames.push(documentName);
        acc[formattedDate].values.push(parseFloat(value_));
      }

      return acc;
    }, {});

    // Filter out invalid dates before calculating latestDate
    const validDates = Object.keys(data).filter(dateStr => dateStr !== 'No Date');
    const latestDate = validDates.length ? new Date(validDates.sort().reverse()[0]) : new Date();

    // Correctly clone latestDate when creating startDate
    let startDate = new Date(latestDate.getTime()); // Use getTime to create a true copy
    if (timeRange === '1month') {
      startDate.setMonth(startDate.getMonth() - 1);
    } else if (timeRange === '6month') {
      startDate.setMonth(startDate.getMonth() - 6);
    } else if (timeRange === '12month') {
      startDate.setMonth(startDate.getMonth() - 12);
    } else if (timeRange === '24month') {
      startDate.setMonth(startDate.getMonth() - 24);
    } // 'forever' includes all data 

    // Generate all periods
    const allPeriods = generateAllPeriods(startDate, latestDate, aggregation);

    // Aggregate data based on aggregation
    const aggregatedData = allPeriods.reduce((acc, period) => {
      acc[period] = { totalValue: 0, count: 0, documentNames: [], values: [] };
      return acc;
    }, {});

    validDates.forEach(date => {
      const dateObj = new Date(date);
      if (dateObj >= startDate || timeRange === 'forever') {
        const period = aggregation === 'day' ? date :
                      aggregation === 'week' ? getWeekStartDate(dateObj) :
                      date.slice(0,7); // month
        if (!aggregatedData[period]) {
          aggregatedData[period] = { totalValue: 0, count: 0, documentNames: [], values: [] };
        }
        aggregatedData[period].totalValue += data[date].totalValue;
        aggregatedData[period].count += data[date].count;
        aggregatedData[period].documentNames = aggregatedData[period].documentNames.concat(data[date].documentNames);
        aggregatedData[period].values = aggregatedData[period].values.concat(data[date].values);
      }
    });

    // Prepare final chart data
    const finalChartData = Object.keys(aggregatedData).map(period => ({
      date: period,
      average: aggregatedData[period].count
        ? aggregatedData[period].totalValue / aggregatedData[period].count
        : 0,
      minimum: aggregatedData[period].values.length ? Math.min(...aggregatedData[period].values) : 0,
      maximum: aggregatedData[period].values.length ? Math.max(...aggregatedData[period].values) : 0,
      sum: aggregatedData[period].totalValue, // Add sum to the data
      documentNames: aggregatedData[period].documentNames,
    }));

    // Ensure that chartData is sorted by date
    finalChartData.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Filter based on showEmptyDates setting
    return showEmptyDates ? finalChartData : finalChartData.filter(item => item.average > 0);
  }, [filteredTimeEvents, tag.variable, aggregation, timeRange, showEmptyDates, showChart]);
  // Request chart data from the server when the chart is shown
  useEffect(() => {
    let cancelled = false;

    if (!showChart) {
      setIsLoadingChart(false);
      return undefined;
    }

    if (!caseid || !tag.variable) {
      setServerChartData([]);
      setServerTimeSeriesData([]);
      setIsTextData(false);
      setIsLoadingChart(false);
      return undefined;
    }

    setIsLoadingChart(true);
    void (async () => {
      try {
        const payload = await fetchProjectChartData(caseid, {
          variable: tag.variable,
          aggregation,
          timeRange,
          showEmptyDates,
        });
        if (cancelled) {
          return;
        }
        setServerChartData(payload.chartData || []);
        setServerTimeSeriesData(payload.timeSeriesData || []);
        if (payload.chartType) {
          setChartType(payload.chartType);
        }
        setIsTextData(payload.dataType === 'categorical');
      } catch (error) {
        if (!cancelled) {
          setServerChartData([]);
          setServerTimeSeriesData([]);
          setIsTextData(false);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingChart(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showChart, aggregation, timeRange, showEmptyDates, caseid, tag.variable]);
  useEffect(() => {
    if (isEditingTag) {
      return;
    }

    setEditedVariable(normalizedVariable);
    setEditedReply(normalizedReply);
    setEditVariableError('');
  }, [isEditingTag, normalizedVariable, normalizedReply]);

  const handleStartEditing = (target = editTarget) => {
    if (!canManageProject || isLockedWidgetTag || isLoadingState) {
      return;
    }
    setEditedVariable(normalizedVariable);
    setEditedReply(normalizedReply);
    setEditTarget(target);
    setIsEditingTag(true);
  };

  const handleCancelEdits = () => {
    setEditedVariable(normalizedVariable);
    setEditedReply(normalizedReply);
    setEditVariableError('');
    setIsEditingTag(false);
  };

  const handleTagOpen = (event) => {
    const requestedTarget = event?.target
      ?.closest?.('[data-tag-edit-target]')
      ?.getAttribute('data-tag-edit-target');
    if ((formattedReply.isJson || isImageTag) && requestedTarget !== 'variable') {
      return;
    }
    handleStartEditing(requestedTarget || 'reply');
  };

  const handleSaveEdits = async () => {
    if (!canManageProject || isLockedWidgetTag || isLoadingState) {
      setIsEditingTag(false);
      return;
    }
    const nextVariable = normalizeTagValue(editedVariable);
    const nextReply = normalizeTagValue(editedReply);
    const updates = {};

    if (nextVariable.trim() === '') {
      setEditedVariable(normalizedVariable);
    } else if (
      nextVariable !== normalizedVariable
      && hasUserTagVariableReservedChars(nextVariable)
    ) {
      setEditVariableError(
        t(
          'settings.variable_no_space_underscore',
          'Tag names cannot contain spaces or reserved metadata suffixes such as _reason or _coordinates.'
        )
      );
      return;
    } else if (nextVariable !== normalizedVariable) {
      updates.variable = nextVariable;
    }

    if (canEditValue) {
      if (nextReply.trim() === '') {
        setEditedReply(normalizedReply);
      } else if (nextReply !== normalizedReply) {
        updates.reply = nextReply;
      }
    }

    if (Object.keys(updates).length) {
      const updatedField = {
        ...tag,
        ...updates,
        needs_review: false,
        review_reason: '',
        confidence: null,
        confidence_source: 'manual',
      };
      setIsSavingTag(true);
      try {
        await updateTimeeventTag(tag?.timeeventid ?? timeevent?.id, updatedField, { lockToken: validationLockToken });
        openSnackbar("Update successfully.", 1000);
        tag.isEdited = 1;
        onUpdate(updatedField);
        if (updates.variable) {
          setEditedVariable(updatedField.variable);
        }
        if (updates.reply) {
          setEditedReply(normalizeTagValue(updatedField.reply));
        }
      } catch (error) {
        openSnackbar(error.message || "Unable to update tag.", 3000, "error");
        return;
      } finally {
        setIsSavingTag(false);
      }
    }

    setIsEditingTag(false);
  };

  const handleEditKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      handleCancelEdits();
    }
  };

  const handleChange = (e, setEdited) => {
    setEdited(e.target.value);
    if (setEdited === setEditedVariable && editVariableError) {
      setEditVariableError('');
    }
  };



  // Handle saving JSON from the JsonViewer component
  const handleSaveJson = async (editedJsonData) => {
    if (!canManageProject || isLockedWidgetTag || isLoadingState) {
      return;
    }
    try {
      const updatedReply = JSON.stringify(editedJsonData);
      
      // Only send update and set edited flag if the value actually changed
      if (updatedReply !== normalizedReply) {
        // Update the reply with the new JSON
        const updatedField = {
          ...tag,
          reply: updatedReply,
          needs_review: false,
          review_reason: '',
          confidence: null,
          confidence_source: 'manual',
        };
        await updateTimeeventTag(tag?.timeeventid ?? timeevent?.id, updatedField, { lockToken: validationLockToken });
        tag.isEdited = 1;
        onUpdate(updatedField);
        
        // Update local state
        setEditedReply(updatedReply);
      }
    } catch (error) {
      throw error;
    }
  };

  // Chart handler functions
  const handleAggregationChange = (e) => {
    setAggregation(e.target.value);
  };
  
  const handleTimeRangeChange = (e) => {
    setTimeRange(e.target.value);
  };
  
  const handleShowEmptyDatesChange = (e) => {
    setShowEmptyDates(e.target.checked);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Use theme-consistent background color but keep variable-specific colors for diversity
  const backgroundColor = generatePastelColor(normalizedVariable);
  const fontColor = normalizedVariable.startsWith('#') ? primaryColor : generateSaturatedColor(normalizedVariable);
  const focusVariable = editTarget === 'variable' || !canEditValue;
  const focusReply = editTarget === 'reply' && canEditValue;
  const shouldRenderTagBody = isLoadingState || hasDisplayableReply || isEditingTag || coordinateItems.length > 0;
  const coordinatesContent = coordinateItems.length > 0 ? (
    <Box
      sx={{
        mt: 1.5,
        pt: 1.5,
        borderTop: '1px solid rgba(87, 87, 176, 0.18)',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.75,
          color: primaryColor,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {t('timeevent.tag_coordinates', 'Coordinates')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {coordinateItems.map((item, index) => (
          <Box
            key={`${item.page}-${index}`}
            sx={{
              p: 1,
              borderRadius: '8px',
              backgroundColor: 'rgba(244, 67, 54, 0.06)',
              border: '1px solid rgba(244, 67, 54, 0.22)',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', color: '#333', fontWeight: 600 }}>
              {t('timeevent.tag_coordinates_page', 'Page {{page}}', { page: item.page })}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#555',
                fontFamily: 'monospace',
                wordBreak: 'break-word',
              }}
            >
              [{item.bbox.map(formatCoordinateNumber).join(', ')}]
            </Typography>
            {item.text && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  color: '#777',
                  wordBreak: 'break-word',
                }}
              >
                {t('timeevent.tag_coordinates_text', 'Matched text')}: {item.text}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  ) : null;

  const shouldUseUnifiedTooltipRenderer = true;
  const confidenceContent = !isLoadingState ? (
    <Box
      sx={{
        mb: 1.25,
        p: 1,
        borderRadius: '8px',
        backgroundColor: hasConfidence ? 'rgba(87, 87, 176, 0.06)' : 'rgba(0, 0, 0, 0.035)',
        border: hasConfidence ? '1px solid rgba(87, 87, 176, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: hasConfidence ? primaryColor : '#666',
          fontWeight: 700,
        }}
      >
        {t('timeevent.tag_confidence', 'Confidence')}: {confidenceDisplayLabel}
      </Typography>
      {confidenceReason && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            color: '#555',
            lineHeight: 1.35,
            wordBreak: 'break-word',
          }}
        >
          {confidenceReasonLabel}: {confidenceReason}
        </Typography>
      )}
    </Box>
  ) : null;
  const reviewContent = needsReview ? (
    <Box
      sx={{
        mb: 1.25,
        p: 1,
        borderRadius: '8px',
        backgroundColor: 'rgba(211, 47, 47, 0.06)',
        border: '1px solid rgba(211, 47, 47, 0.22)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#d32f2f',
            flexShrink: 0,
          }}
        />
        <Typography variant="caption" sx={{ color: '#b71c1c', fontWeight: 700 }}>
          {t('timeevent.tag_needs_review', 'Needs review')}
        </Typography>
      </Box>
    </Box>
  ) : null;
  const tooltipContent = isLoadingState ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
      <CircularProgress size={14} thickness={5} sx={{ color: primaryColor }} />
      <Typography variant="body2" sx={{ color: primaryColor, fontStyle: 'italic' }}>
        {loadingLabel}
      </Typography>
    </Box>
  ) : (
    <Box>
      {confidenceContent}
      {reviewContent}
      <UnifiedAnswerRenderer
        tag={tag}
        value={displayReply}
        widgetId={`timeevent-tag-preview-${displayVariable || tag.id || tag.variable}`}
        onImageClick={handleOpenImageViewer}
        disableInternalScroll={true}
      />
      {coordinatesContent}
    </Box>
  );
  const editContent = isEditingTag ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField
        label={t('timeevent.tag_name', 'Tag name')}
        variant="outlined"
        size="small"
        value={editedVariable}
        onChange={(e) => handleChange(e, setEditedVariable)}
        onKeyDown={handleEditKeyDown}
        autoFocus={focusVariable}
        fullWidth
        error={!!editVariableError}
        helperText={editVariableError}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      />
      {canEditValue ? (
        <TextField
          label={t('timeevent.tag_value', 'Value')}
          variant="outlined"
          size="small"
          value={editedReply}
          onChange={(e) => handleChange(e, setEditedReply)}
          onKeyDown={handleEditKeyDown}
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          autoFocus={focusReply}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            },
          }}
        />
      ) : (
        <Typography variant="caption" sx={{ color: '#666' }}>
          {isImageTag ? 'Use the image viewer to edit the value.' : 'Use the JSON viewer to edit the value.'}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          size="small"
          onClick={handleCancelEdits}
          disabled={isSavingTag}
          sx={{ color: primaryColor, minHeight: { xs: 44, sm: 30 } }}
        >
          {t('timeevent.tag_cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSaveEdits}
          disabled={isSavingTag}
          sx={{
            minWidth: 76,
            minHeight: { xs: 44, sm: 30 },
            backgroundColor: primaryColor,
            '&:hover': {
              backgroundColor: hoverColor,
            },
          }}
        >
          {isSavingTag ? <CircularProgress size={16} color="inherit" /> : t('timeevent.tag_save', 'Save')}
        </Button>
      </Box>
    </Box>
  ) : null;

  // Don't render if it's an empty array or empty string (unless editing or it's an image tag)
  if (isEmptyArray && !isEditingTag && !isImageTag) {
    return null;
  }

  return (
    <TimeEventTableViewer
      title={displayVariable}
      data={formattedReply.raw}
      fieldId={tag.id}
      onSave={canManageProject ? handleSaveJson : undefined}
      caseid={caseid}
      startInEditMode={canManageProject && !isLockedWidgetTag && formattedReply.isJson}
    >
      {(openJsonViewer) => (
        <>
          {shouldRenderTagBody && (
            <TagTooltip
              data={isLoadingState ? loadingLabel : (displayReply || '(no data)')}
              dataType={isImageTag ? 'image' : 'text'}
              customStyles={{
                width: 'min(900px, calc(100vw - 24px))',
                maxWidth: 'min(900px, calc(100vw - 24px))',
                maxHeight: 'min(84vh, 760px)'
              }}
              contentRenderer={shouldUseUnifiedTooltipRenderer ? tooltipContent : null}
              onImageClick={isImageTag && !isLoadingState ? handleOpenImageViewer : null}
              showActions={!isLoadingState}
              onShowChart={!isLoadingState ? () => setShowChart(true) : null}
              onShowInfo={!isLoadingState ? () => setOpenProofDialog(true) : null}
              onDelete={!isLoadingState && !isLockedWidgetTag && canManageProject ? handleDeleteClick : null}
              onShowJsonViewer={!isLoadingState ? openJsonViewer : null}
              jsonViewerLabel={canManageProject && !isLockedWidgetTag
                ? t('timeevent.tag_edit_all_data', 'Edit all data')
                : t('timeevent.tag_view_all_data', 'View all data')}
              onEdit={!isLoadingState && !isLockedWidgetTag && canManageProject && !isEditingTag ? handleStartEditing : null}
              extraContent={!isLoadingState && !isLockedWidgetTag && canManageProject ? editContent : null}
              onClose={handleCancelEdits}
              onOpen={handleTagOpen}
              title={displayVariable}
              triggerAriaLabel={t('timeevent.tag_open_details', 'Open tag details: {{variable}}', { variable: displayVariable })}
              triggerComponent="div"
              triggerSx={{
                width: '100%',
                '&:focus-visible': {
                  outline: `2px solid ${primaryColor}`,
                  outlineOffset: '2px',
                  borderRadius: '12px',
                },
              }}
              tooltipPlacement="bottom-start"
              openOnClick={true}
              forceTooltip={true}
            >
              <StyledBox
                backgroundColor={backgroundColor}
                title={tagTitle}
                onPointerEnter={(event) => {
                  if (event.pointerType && event.pointerType !== 'mouse') return;
                  dispatchTimeEventTagHover(tag);
                  if (openViewerOnHover) {
                    scheduleViewerOpen();
                  }
                }}
                onPointerLeave={(event) => {
                  if (event.pointerType && event.pointerType !== 'mouse') return;
                  cancelScheduledViewerOpen();
                  dispatchTimeEventTagHover(null);
                }}
                sx={isViewerHoveredTag ? {
                  boxShadow: '0 0 0 2px rgba(87, 87, 176, 0.52), 0 0 0 5px rgba(87, 87, 176, 0.12), 0 10px 20px rgba(87, 87, 176, 0.22)',
                  background: `linear-gradient(135deg, #ffffff 0%, ${primaryColor}24 100%)`,
                  outline: `2px solid ${primaryColor}`,
                  outlineOffset: '1px',
                  '& .MuiTypography-root': {
                    color: `${primaryColor} !important`,
                  },
                  '& .MuiTypography-caption': {
                    fontWeight: 700,
                  },
                } : undefined}
              >
                {needsReview && (
                  <Tooltip title={reviewTooltipLabel}>
                    <Box
                      aria-label={t('timeevent.tag_needs_review', 'Needs review')}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        backgroundColor: '#d32f2f',
                        border: '1px solid #fff',
                        boxShadow: '0 0 0 1px rgba(211, 47, 47, 0.35)',
                        zIndex: 2,
                      }}
                    />
                  </Tooltip>
                )}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                width: '100%',
                overflow: 'hidden',
                cursor: 'pointer'
              }}>
              {/* Variable name with icon */}
              <Box
                data-tag-edit-target="variable"
                onClick={() => setEditTarget('variable')}
                sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px', // Reduced gap to 1px for tighter spacing
                flexShrink: 0
              }}>
                {/* Enhanced Icon display with fallback */}
                <Box sx={{
                  width: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  backgroundColor: (tag.icon || isImageTag) ? 'white' : 'transparent',
                  borderRadius: '40%', // Less round - changed from 50% to 10%
                  border: (tag.icon || isImageTag) ? `1px solid ${primaryColor}15` : 'none',
                  boxShadow: (tag.icon || isImageTag) ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': (tag.icon || isImageTag) ? {
                    backgroundColor: 'white',
                    borderColor: `${primaryColor}30`,
                    transform: 'scale(1.05)',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                  } : {}
                }}>
                  {isImageTag ? (
                    <Typography sx={{ 
                      fontSize: '12px', 
                      lineHeight: 1,
                      filter: 'grayscale(0.3)'
                    }}>
                      📷
                    </Typography>
                  ) : tag.icon ? (
                    <>
                      {/* Show icon image on desktop only */}
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <img 
                          src={`/images/icons/${tag.icon}`}
                          alt={normalizedVariable}
                          style={{
                            width: '16px',
                            height: '16px',
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            // Show a fallback icon if image fails to load
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `
                              <div style="
                                width: 10px; 
                                height: 10px; 
                                background: ${primaryColor};
                                border-radius: 50%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                color: white;
                                font-size: 7px;
                                font-weight: bold;
                              ">
                                ${normalizedVariable.charAt(0).toUpperCase()}
                              </div>
                            `;
                          }}
                        />
                      </Box>
                      {/* Show letter badge on mobile */}
                      <Box 
                        sx={{
                          display: { xs: 'flex', sm: 'none' },
                          width: '22px',
                          height: '22px',
                          backgroundColor: `${fontColor}20`,
                          borderRadius: '50%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: fontColor,
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: `${fontColor}30`,
                            transform: 'scale(1.05)',
                          },
                          transition: 'all 0.2s ease',
                        }}>
                        {normalizedVariable.charAt(0).toUpperCase()}
                      </Box>
                    </>
                  ) : (
                    // Subtle placeholder when no icon is set - larger on mobile
                    <Box 
                      sx={{
                        width: { xs: '22px', sm: '10px' },
                        height: { xs: '22px', sm: '10px' },
                        backgroundColor: `${fontColor}20`,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: fontColor,
                        fontSize: { xs: '12px', sm: '7px' },
                        fontWeight: 'bold',
                        opacity: { xs: 1, sm: 0.7 },
                        cursor: { xs: 'pointer', sm: 'default' },
                        '&:hover': {
                          backgroundColor: { xs: `${fontColor}30`, sm: `${fontColor}20` },
                          transform: { xs: 'scale(1.05)', sm: 'none' },
                        },
                        transition: 'all 0.2s ease',
                      }}>
                      {normalizedVariable.charAt(0).toUpperCase()}
                    </Box>
                  )}
                </Box>
                
                <Tooltip title={tag.description || displayVariable}>
                  <Typography
                    variant="caption"
                    sx={{ 
                      fontWeight: '600', 
                      color: fontColor,
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      display: 'block',
                      whiteSpace: showFullValue ? 'normal' : 'nowrap',
                      overflow: showFullValue ? 'visible' : 'hidden',
                      textOverflow: showFullValue ? 'clip' : 'ellipsis',
                      maxWidth: showFullValue ? 'none' : { xs: '92px', sm: '120px' },
                    }}
                  >
                    {displayVariable}
                  </Typography>
                </Tooltip>
              </Box>

              {/* Separator - hidden on mobile */}
              <Typography
                variant="caption"
                sx={{
                  color: '#999',
                  fontWeight: '400',
                  flexShrink: 0,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                |
              </Typography>              {/* Value display */}              {isLoadingState ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: '1 1 auto',
                    minWidth: 0,
                    maxWidth: { xs: '100%', sm: '220px' },
                  }}
                >
                  <CircularProgress size={13} thickness={5} sx={{ color: primaryColor }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: primaryColor,
                      fontSize: '0.7rem',
                      fontStyle: 'italic',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {loadingLabel}
                  </Typography>
                </Box>
              ) : String(displayReply || '').trim() !== '' && (
                  <Typography
                    data-tag-edit-target="reply"
                    data-tag-tooltip-ignore={formattedReply.isJson ? 'true' : undefined}
                    variant="caption"
                    role={formattedReply.isJson ? 'button' : undefined}
                    tabIndex={formattedReply.isJson ? 0 : undefined}
                    aria-label={formattedReply.isJson
                      ? `${t('timeevent.tag_view_all_data', 'View all data')}: ${formattedReply.short}`
                      : undefined}
                    sx={{ 
                      fontStyle: isImageTag ? 'normal' : (formattedReply.isJson ? 'normal' : 'italic'),
                      color: isImageTag ? '#e91e63' : (formattedReply.isJson ? primaryColor : '#666'),
                      fontSize: '0.7rem',
                      fontWeight: isImageTag ? '600' : (formattedReply.isJson ? '600' : '400'),
                      cursor: 'pointer',
                      overflow: showFullValue ? 'visible' : 'hidden',
                      textOverflow: showFullValue ? 'clip' : 'ellipsis',
                      whiteSpace: showFullValue ? 'pre-wrap' : 'nowrap',
                      flex: '1 1 auto',
                      minWidth: 0,
                      maxWidth: showFullValue ? 'none' : { xs: '100%', sm: '200px' },
                      overflowWrap: showFullValue ? 'anywhere' : 'normal',
                      '&:hover': {
                        color: isImageTag ? '#c2185b' : (formattedReply.isJson ? selectedColor : '#333'),
                        textDecoration: 'underline',
                      },
                    }}
                    onClick={(event) => {
                      if (formattedReply.isJson) {
                        event.stopPropagation();
                        openJsonViewer(event);
                        return;
                      }
                      setEditTarget('reply');
                    }}
                    onKeyDown={(event) => {
                      if (formattedReply.isJson && ['Enter', ' '].includes(event.key)) {
                        event.preventDefault();
                        event.stopPropagation();
                        openJsonViewer(event);
                      }
                    }}
                  >
                    {isImageTag ? 
                      "🖼️ View Image" :
                      (showFullValue ? String(displayReply) : formattedReply.isJson ?
                        formattedReply.short :
                        `${String(displayReply).substring(0, 25)}${String(displayReply).length > 25 ? '...' : ''}`
                      )
                    }
                  </Typography>
              )}

              {/* JSON indicator for empty/no reply */}
              {!isLoadingState && String(displayReply || '').trim() === '' && (
                <Typography
                  data-tag-edit-target="reply"
                  variant="caption"
                  sx={{ 
                    color: '#999',
                    fontSize: '0.7rem',
                    fontStyle: 'italic',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                    maxWidth: '140px',
                    '&:hover': {
                      color: '#666',
                    },
                  }}
                  onClick={() => setEditTarget('reply')}
                >
                  {t('timeevent.tag_add_value', 'Add value')}
                </Typography>
              )}
              {coordinateItems.length > 1 && (
                <Box data-tag-tooltip-ignore="true" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                  <Button
                    size="small"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => { event.stopPropagation(); selectEvidence(activeEvidenceIndex - 1); }}
                    sx={{ minWidth: 22, px: 0.25, fontSize: '0.75rem' }}
                  >‹</Button>
                  <Typography variant="caption" sx={{ minWidth: 28, textAlign: 'center', fontWeight: 700 }}>
                    {activeEvidenceIndex + 1}/{coordinateItems.length}
                  </Typography>
                  <Button
                    size="small"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => { event.stopPropagation(); selectEvidence(activeEvidenceIndex + 1); }}
                    sx={{ minWidth: 22, px: 0.25, fontSize: '0.75rem' }}
                  >›</Button>
                </Box>
              )}
              {enableTouchHighlight && coordinateItems.length > 0 && (
                <IconButton
                  data-tag-tooltip-ignore="true"
                  size="small"
                  aria-label={t(
                    isTouchHighlightSelected
                      ? 'timeevent.tag_clear_viewer_highlight'
                      : 'timeevent.tag_highlight_evidence',
                    isTouchHighlightSelected ? 'Clear evidence highlight' : 'Show evidence in viewer'
                  )}
                  aria-pressed={isTouchHighlightSelected}
                  title={t(
                    isTouchHighlightSelected
                      ? 'timeevent.tag_clear_viewer_highlight'
                      : 'timeevent.tag_highlight_evidence',
                    isTouchHighlightSelected ? 'Clear evidence highlight' : 'Show evidence in viewer'
                  )}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={handleTouchHighlight}
                  sx={{
                    display: 'inline-flex',
                    '@media (min-width: 600px)': {
                      display: 'none',
                    },
                    '@media (pointer: coarse)': {
                      display: 'inline-flex',
                    },
                    minWidth: 40,
                    minHeight: 40,
                    p: 0.75,
                    flexShrink: 0,
                    color: isTouchHighlightSelected ? primaryColor : 'text.secondary',
                    backgroundColor: isTouchHighlightSelected ? `${primaryColor}14` : 'transparent',
                    '&:hover': {
                      backgroundColor: `${primaryColor}18`,
                    },
                  }}
                >
                  {isTouchHighlightSelected ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              )}
            </Box>
              </StyledBox>
            </TagTooltip>
          )}

      {/* Chart Modal */}
      <Dialog
        open={showChart}
        onClose={() => setShowChart(false)}
        maxWidth={chartType === 'data' ? 'lg' : 'md'}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            fontFamily: 'Roboto, sans-serif',
            maxHeight: '90vh',
            width: chartType === 'data' ? '95%' : '90%'
          }
        }}
      >
        <DialogContent sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1
            }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: primaryColor, 
                  fontWeight: 'bold',
                  flexGrow: 1,
                  textAlign: 'center'
                }}
              >
                {tag.variable}
              </Typography>
              <CloseButton
                ariaLabel="close"
                onClick={() => setShowChart(false)}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                }}
              />
            </Box>
            <Divider sx={{ my: 2 }} />
            
            {/* Chart controls */}
            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', mb: 2, gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="aggregation-label" sx={{ color: primaryColor }}>Group By</InputLabel>
                <Select
                  labelId="aggregation-label"
                  value={aggregation}
                  onChange={handleAggregationChange}
                  label="Group By"
                  disabled={chartType === 'data'}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: `${primaryColor}60`,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: hoverColor,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: primaryColor,
                    },
                    color: primaryColor
                  }}
                >
                  <MenuItem value="day">Day</MenuItem>
                  <MenuItem value="week">Week</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="timerange-label" sx={{ color: primaryColor }}>Time Range</InputLabel>
                <Select
                  labelId="timerange-label"
                  value={timeRange}
                  onChange={handleTimeRangeChange}
                  label="Time Range"
                  disabled={chartType === 'data'}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: `${primaryColor}60`,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: hoverColor,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: primaryColor,
                    },
                    color: primaryColor
                  }}
                >
                  <MenuItem value="1month">Last 1 Month</MenuItem>
                  <MenuItem value="6month">Last 6 Months</MenuItem>
                  <MenuItem value="12month">Last 12 Months</MenuItem>
                  <MenuItem value="24month">Last 24 Months</MenuItem>
                  <MenuItem value="forever">All Time</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={showEmptyDates}
                    onChange={handleShowEmptyDatesChange}
                    color="primary"
                    disabled={chartType === 'data'}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: primaryColor,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: primaryColor,
                      },
                    }}
                  />
                }
                label="Show Empty Dates"
                sx={{ color: '#555' }}
              />
            </Box>

            {/* Chart type selector */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <ToggleButtonGroup
                value={chartType}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue) setChartType(newValue);
                }}
                aria-label="chart type"
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: primaryColor,
                    '&.Mui-selected': {
                      backgroundColor: `${primaryColor}20`,
                      color: primaryColor,
                      fontWeight: 'bold'
                    }
                  }
                }}
              >
                <ToggleButton value="line" aria-label="line chart">
                  <ShowChartIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Line
                </ToggleButton>
                <ToggleButton value="pie" aria-label="pie chart">
                  <DonutLargeIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Pie
                </ToggleButton>
                <ToggleButton value="data" aria-label="data table">
                    <TableChartIcon sx={{ mr: 0.5 }} fontSize="small" />
                    Data
                  </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            
            {/* Chart component - Using server-side data when available */}
            {isLoadingChart ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography>Loading chart data...</Typography>
              </Box>
            ) : chartType === 'data' ? (
              <Box>
                <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: lightPurple }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: lightPurple }}>Document</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: lightPurple }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row, index) => {
                          // Check if the value is potentially a JSON array
                          let valueIsArray = false;
                          let arrayValues = [];
                          
                          try {
                            const parsedValue = JSON.parse(row.value);
                            if (Array.isArray(parsedValue)) {
                              valueIsArray = true;
                              arrayValues = parsedValue;
                            }
                          } catch (e) {
                            // Not a valid JSON, treat as a regular value
                          }
                          
                          // If it's an array, create a row for each item
                          if (valueIsArray && arrayValues.length > 0) {
                            return arrayValues.map((item, itemIndex) => (                              <TableRow 
                                key={`${index}-${itemIndex}`}
                                sx={{ 
                                  '&:nth-of-type(odd)': { backgroundColor: '#f9f9ff' },
                                  // First row of the array has normal styling, others are visually grouped
                                  ...(itemIndex > 0 ? { 
                                    borderTop: 'none',
                                    '& td:first-of-type': { 
                                      color: 'transparent',  // Hide repeated date for visual grouping
                                    },
                                    '& td:nth-of-type(2)': { 
                                      color: 'transparent', // Hide repeated document name for visual grouping
                                    }
                                  } : {})
                                }}
                              >
                                <TableCell>
                                  {itemIndex === 0 ? row.formattedDate : ''}
                                </TableCell>
                                <TableCell 
                                  sx={{ 
                                    maxWidth: '400px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {itemIndex === 0 ? (
                                    <Tooltip title={row.documentName}>
                                      <Typography variant="body2" noWrap>
                                        {row.documentName}
                                      </Typography>
                                    </Tooltip>
                                  ) : ''}
                                </TableCell>
                                <TableCell>
                                  {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                                </TableCell>
                              </TableRow>
                            ));
                          }
                          
                          // For regular values, just return a single row
                          return (
                            <TableRow 
                              key={index}
                              hover
                              sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9ff' } }}
                            >
                              <TableCell>{row.formattedDate}</TableCell>
                              <TableCell 
                                sx={{ 
                                  maxWidth: '400px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <Tooltip title={row.documentName}>
                                  <Typography variant="body2" noWrap>
                                    {row.documentName}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>{row.value}</TableCell>
                            </TableRow>
                          );
                        })}
                      {tableData.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">No data found</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={tableData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      m: 0,
                    },
                  }}
                />
              </Box>
            ) : chartType === 'pie' ? (
              <PieChartComponent 
                data={Array.isArray(serverChartData) && serverChartData.length > 0 ? serverChartData : chartData}
                groupBy="date"
                valueField="sum"
              />
            ) : (
              <LineChartComponent 
                data={Array.isArray(serverChartData) && serverChartData.length > 0 ? serverChartData : chartData} 
                timeSeriesData={Array.isArray(serverTimeSeriesData) && serverTimeSeriesData.length > 0 ? serverTimeSeriesData : []}
                tooltipFormatter={(item) => `Sum: ${item.sum}`} 
                dataType={isTextData ? 'categorical' : 'numerical'}
              />
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              {chartType === 'data' && (
                <Typography variant="caption" color="text.secondary">
                  
                  Showing {tableData.length} records
                </Typography>
              )}
              <Box sx={{ marginLeft: 'auto' }}>
                <Button 
                  onClick={() => setShowChart(false)}
                  variant="outlined"
                  sx={{
                    color: primaryColor,
                    borderColor: primaryColor,
                    '&:hover': {
                      backgroundColor: lightPurple,
                      borderColor: hoverColor,
                    }                  }}
                >
                  Close
                </Button>
              </Box>
            </Box>
        </DialogContent>
      </Dialog>

        {/* Proof Information Dialog */}
        <TimeEventProof
          open={openProofDialog}
          onClose={() => setOpenProofDialog(false)}
          timeEvent={timeevent}
          field={tag}
        />

        {/* Image Viewer Dialog */}
        <Dialog
          open={openImageViewer}
          onClose={handleCloseImageViewer}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            }
          }}
        >
          <DialogContent sx={{ "--dialog-content-top-padding": "0px", p: 0 }}>
            <Box sx={{ position: 'relative', width: '100%', paddingTop: '75%' }}>
              <img 
                src={imageToShow}
                alt="Preview"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <Box sx={{ 
                display: 'none',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '8px',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: 1
              }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  ❌ Unable to load image
                </Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  Invalid image data
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: '8px 16px' }}>
            <Button onClick={handleCloseImageViewer} sx={{ color: '#666' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={openDeleteDialog}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          isLoading={isDeletingTag}
          title="Confirm Delete"
          message={`Are you sure you want to delete the variable "${tag?.variable}"?`}
        />
        </>
      )}
    </TimeEventTableViewer>
  );
};

export default TimeEventTag;
